import { z } from 'zod/v4';
import {
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  REQUEST_FILTER_MAX_LENGTH,
} from '../constants.js';
import { QUESTIONNAIRE_REQUEST_STATUSES } from './questionnaire-request-status.js';

/**
 * Sort columns the request list accepts — mirrors `SORT_COLUMNS_ENUM` in the
 * legacy `routes/schemas/v1/request/request.js`. Each key maps to a SQL
 * expression in `core/db/questionnaire-requests/filters.ts` (`SORT_COLUMNS`);
 * keep the two in sync when adding or removing a column.
 */
export const QuestionnaireRequestsListSortColumns = [
  'status',
  'dueDate',
  'updatedAt',
  'createdAt',
  'company',
  'lastReminded',
  'supplierName',
  'answeredByName',
  'facilityProcesses',
  'progress',
  'supplyChainTier',
  'relationshipType',
  'collaborationStatus',
  'country',
  'agents',
  'vendors',
  'internalCompanyCode',
  'notes',
] as const;

export type QuestionnaireRequestsListSortColumn =
  (typeof QuestionnaireRequestsListSortColumns)[number];

/**
 * Filter columns the request list accepts — mirrors `FILTER_COLUMNS_ENUM` in the
 * same legacy file, and `FILTER_COLUMNS` in `filters.ts`. Narrower than the sort
 * list: `dueDate`, `createdAt`, `updatedAt`, `lastReminded` and `progress` are
 * sortable but not filterable.
 */
export const QuestionnaireRequestsListFilterColumns = [
  'status',
  'company',
  'supplierName',
  'answeredByName',
  'facilityProcesses',
  'supplyChainTier',
  'relationshipType',
  'collaborationStatus',
  'country',
  'agents',
  'vendors',
  'internalCompanyCode',
  'notes',
] as const;

/**
 * `column:DIRECTION` pairs, comma-separated.
 *
 * Deliberately **not** an enum of the sort columns. The legacy route declared
 * its query schema under the key `queryString` — which Fastify does not
 * recognise — so nothing was ever validated, and at least one caller sends a
 * column that was never in the enum (`companyName:ASC`). An enum here would 400
 * on input that works today; `buildOrderBy` drops unknown columns instead and
 * falls back to the default order. Lowercase directions are accepted as a
 * superset — the builder upper-cases.
 */
const sortParamSchema = z
  .string()
  .regex(/^[a-zA-Z]+:(ASC|DESC|asc|desc)(,[a-zA-Z]+:(ASC|DESC|asc|desc))*$/)
  .optional();

export const QuestionnaireRequestsListParamsSchema = z.object({
  id: z.string().min(1),
});

export type QuestionnaireRequestsListParams = z.infer<
  typeof QuestionnaireRequestsListParamsSchema
>;

export const QuestionnaireRequestsListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  sort: sortParamSchema,
  /**
   * JSON-encoded filter object. Capped well above the questionnaires list's 512
   * because frontend-v2 comma-joins seven multi-value filter keys into this one
   * param, and a realistic multi-select exceeds 512 characters.
   */
  filter: z.string().max(REQUEST_FILTER_MAX_LENGTH).optional(),
});

export type QuestionnaireRequestsListQuery = z.infer<
  typeof QuestionnaireRequestsListQuerySchema
>;

/**
 * Answer statistics for one request.
 *
 * A **closed** object on purpose: the legacy response schema declared
 * `additionalProperties: false` here, so the serialiser truncated the stored
 * blob to exactly these keys. Zod strips undeclared keys, reproducing that.
 * `RequestRowProgress.tsx` in frontend-v2 reads all twelve.
 */
export const QuestionnaireRequestStatisticsSchema = z.object({
  totalApprovedResponses: z.number().optional(),
  totalInReviewResponses: z.number().optional(),
  totalDeniedResponses: z.number().optional(),
  totalMissingResponses: z.number().optional(),
  totalResponses: z.number().optional(),
  totalQuestions: z.number().optional(),
  totalComments: z.number().optional(),
  approvedResponsesPercentage: z.number().min(0).max(100).optional(),
  inReviewResponsesPercentage: z.number().min(0).max(100).optional(),
  deniedResponsesPercentage: z.number().min(0).max(100).optional(),
  missingResponsesPercentage: z.number().min(0).max(100).optional(),
  responsesPercentage: z.number().min(0).max(100).optional(),
});

export type QuestionnaireRequestStatistics = z.infer<
  typeof QuestionnaireRequestStatisticsSchema
>;

/**
 * A company reference list read off a company profile (`agents`, `vendors`).
 *
 * The union is not defensiveness, it is the two shapes that genuinely occur.
 * The SELECT reads `(supplier.data->'agents')->'value'` — the `->` operator
 * yields **jsonb**, which node-postgres parses into a real JS array, so that is
 * what current rows send. Legacy profiles that stored the blob as text still
 * arrive as a JSON string, exactly as `supplyChainTier` does in the transformer.
 *
 * Declaring only one half would either 500 on the other or hand the frontends a
 * type that is wrong half the time — which is what happened: v2's
 * `companyRefIds` calls `JSON.parse` on the value, so against a parsed array it
 * throws and the Agents / Vendors columns silently render empty.
 */
const CompanyRefListSchema = z.union([
  z.array(z.looseObject({ id: z.string() })),
  z.string(),
]);

/**
 * Row shape returned by the per-questionnaire request list. `looseObject`
 * because the legacy item schema was `additionalProperties: true`.
 *
 * **This schema is the frontends' type for these rows** — it is imported by
 * `frontend-v2/src/api/questionnaires/types.ts` rather than re-declared there,
 * so a field the FE reads must be declared here or it infers as `unknown`. The
 * prose "field reads in the frontends" list this docblock used to carry was that
 * contract kept where no compiler could check it; the declarations below replace
 * it. Columns nothing reads stay undeclared and ride along on the `looseObject`,
 * because declaring a field makes the serialiser *validate* it.
 *
 * Timestamps are `z.string()` because `transformQuestionnaireRequestRow`
 * ISO-normalises them before the serialiser sees them — see `toIso` there.
 *
 * `facilityProcesses` is a **comma-separated string of ids**, not an array — the
 * transformer flattens the profile's `[{id}]` blob. `relationshipType` falls
 * back to `{}` rather than `[]`. Both are legacy shapes the frontends rely on,
 * and both are declared as such rather than tidied.
 */
export const QuestionnaireRequestListRowSchema = z.looseObject({
  id: z.string(),
  // ─── NOT NULL in the database ──────────────────────────────────────────────
  // `NOT NULL` on `supplier_questionnaire_service_requests` and selected by
  // every query feeding this schema, so they are declared required. They were
  // `.nullable().optional()` — looser than the database, and looser than what
  // the frontends had always assumed. `status` keeps an open value domain: the
  // column has no CHECK constraint, so the closed union the frontend used to
  // declare was an assumption about the *shape*; the value domain is closed
  // below.
  //
  // `status` is an enum, not a bare string. The column has no CHECK constraint,
  // but on this platform that is the norm — value domains are enforced in the
  // application layer on both sides, and this module already closes this one on
  // the way *in* (`UpdateQuestionnaireRequestStatusBodySchema`). Closing it on
  // the way out makes one definition serve the write path, the read path and
  // both frontends.
  //
  // The enum carries all five values including `DELETED`, because that is the
  // column's real domain. The lists cannot return it — every `DELETED` row in
  // staging has `is_removed = 1`, and both queries filter `is_removed = 0`, so
  // the visible set is the four the frontends label. Declaring only four would
  // make the type describe the query rather than the column.
  questionnaireId: z.string(),
  forCompanyId: z.string(),
  onBehalfOfCompanyId: z.string(),
  status: z.enum(QUESTIONNAIRE_REQUEST_STATUSES),
  supplierName: z.string().nullable().optional(),
  answeredByName: z.string().nullable().optional(),
  supplierConnected: z.boolean().nullable().optional(),
  answeredByConnected: z.boolean().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
  facilityProcesses: z.string().nullable().optional(),
  collaborationStatus: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  internalCompanyCode: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  dueDate: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  lastReminded: z.string().nullable().optional(),
  // ─── Company-profile fields ────────────────────────────────────────────────
  // The four below are read out of `companies_service_company_profiles.data`, a
  // free-form jsonb blob owned by another feature. Declaring them is what gives
  // the frontends a real type, but it also means the serialiser now *validates*
  // values this module does not write — and a surprising legacy blob would 500
  // the whole list rather than render one empty cell.
  //
  // `.catch(null)` is the deliberate resolution: a value that does not match
  // degrades to `null`, which every consumer already handles as "no profile
  // data". The shape is declared; the blast radius is not.

  // A `null` tier stays `null` rather than becoming `[]` — both frontends rely
  // on that to tell "no profile data" from "empty selection".
  supplyChainTier: z.array(z.string()).nullable().optional().catch(null),
  // `{}` (not `[]`) is the transformer's documented fallback, so the union is
  // the real shape. The frontends already treat a non-array as "nothing
  // selected"; this makes the compiler agree.
  relationshipType: z
    .union([z.array(z.looseObject({ id: z.string() })), z.looseObject({})])
    .nullable()
    .optional()
    .catch(null),
  agents: CompanyRefListSchema.nullable().optional().catch(null),
  vendors: CompanyRefListSchema.nullable().optional().catch(null),
  statistics: QuestionnaireRequestStatisticsSchema.nullable().optional(),
});

export type QuestionnaireRequestListRow = z.infer<
  typeof QuestionnaireRequestListRowSchema
>;

/**
 * The rows of the answer requests list.
 *
 * Just the array — the `{metadata, data}` envelope around it comes from
 * `buildResponseSchemas({ 200: { type: 'data', schema, paginated: true } })` on the
 * route, the same builder every other paginated route on the platform uses. Nothing
 * here describes the envelope, so there is one definition of it rather than six.
 */
export const QuestionnaireRequestsListResponseSchema = z.array(
  QuestionnaireRequestListRowSchema,
);

export type QuestionnaireRequestsListResponse = z.infer<
  typeof QuestionnaireRequestsListResponseSchema
>;
